import classes from './login-stage-two-lyra.module.scss';

import { Box, IconButton, InputAdornment, Link } from '@mui/material';
import {
  CcfBox,
  CcfButton,
  CcfTextField,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useState } from 'react';

export interface LoginStageTwoLyraProps {
  navigateToUsernameLoginScreen: () => void;
  onForgotPasswordClick: () => void;
  currentpassword: string;
  currentusername: string;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error: {
    message: string;
    status: boolean;
  };
  checkUserNameAndPassword: (e: React.FormEvent<HTMLFormElement>) => void;
}

/**
 * Component for login stage two (check password and navigate) styled for the Lyra design
 * @param props - LoginStageTwoLyraProps
 * @example <LoginStageTwoLyra />
 * @returns
 */
export function LoginStageTwoLyra(props: LoginStageTwoLyraProps) {
  const {
    navigateToUsernameLoginScreen,
    onForgotPasswordClick,
    currentusername,
    currentpassword,
    onPasswordChange,
    checkUserNameAndPassword,
    error,
  } = props;
  const [translate] = useTranslator();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Box>
      <CcfTypography className={classes.welcomeTitle}>
        {translate('enterYourPasswordTitle')}
      </CcfTypography>
      <CcfBox className={classes.passwordUserRow}>
        <span className={classes.avatarCircle}>
          <AccountCircleIcon fontSize="small" />
        </span>
        <CcfTypography className={classes.usernameText}>
          {currentusername}
        </CcfTypography>
      </CcfBox>
      <form onSubmit={checkUserNameAndPassword}>
        <CcfTextField
          id="password"
          placeholder={translate('password')}
          type={showPassword ? 'text' : 'password'}
          size="small"
          variant="outlined"
          fullWidth
          autoFocus
          value={currentpassword}
          onChange={onPasswordChange}
          error={error.status}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {error.status && (
          <Box className={classes.errorRow}>
            <ErrorOutlineIcon
              fontSize="small"
              className={classes.errorIcon}
            />
            <CcfTypography className={classes.errorText}>
              {translate('passwordErrorMessage')}
            </CcfTypography>
          </Box>
        )}
        <CcfButton
          type="submit"
          variant="contained"
          fullWidth
          disabled={currentpassword.length === 0}
          className={
            currentpassword.length === 0
              ? classes.continueButtonDisabled
              : classes.continueButton
          }
        >
          <CcfTypography translationKey="signInButtonText" />
        </CcfButton>
      </form>
      <CcfButton
        variant="outlined"
        fullWidth
        className={classes.backButton}
        startIcon={<ArrowBackIosIcon fontSize="inherit" />}
        onClick={navigateToUsernameLoginScreen}
      >
        <CcfTypography translationKey="backButtonText" />
      </CcfButton>
      <Link
        className={classes.forgotPasswordLink}
        onClick={onForgotPasswordClick}
      >
        <CcfTypography
          variant="inherit"
          translationKey="loginStageTwoForgotPassword"
        />
      </Link>
    </Box>
  );
}

export default LoginStageTwoLyra;
